import { CookieKey } from '@/constants';
import Cookies from 'js-cookie';
import { resetUserCookies } from '../cookies.utils';
import { refreshToken as refreshTokenFetcher } from './tokenRefresher';

/**
 * Promise to track the current token refresh operation
 * When it's null, no refresh is in progress
 * When it's a Promise, it represents the current refresh operation
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Get the current refresh promise if one exists
 * @returns The current refresh promise or null if no refresh is in progress
 */
export const getRefreshPromise = async () => refreshPromise;

/**
 * Refresh the authentication token using the refresh token stored in cookies
 * This function:
 * 1. Gets the refresh token from cookies
 * 2. Makes a request to refresh the access token
 * 3. Stores the new tokens in cookies
 * 4. Returns the new access token
 *
 * @returns Promise<string> - New access token
 * @throws Error if refresh token is missing or refresh request fails
 */
const refreshAuthToken = async (tokenToRefresh: string): Promise<string> => {
  try {
    if (!tokenToRefresh) {
      throw new Error('No refresh token available');
    }

    const response = await refreshTokenFetcher(tokenToRefresh);

    // Store new tokens in cookies for subsequent requests
    Cookies.set(CookieKey.AccessToken, response.access_token);

    return response.access_token;
  } catch (error) {
    // If refresh fails, clear all tokens to force re-authentication
    resetUserCookies();
    // Redirect to 403 page on refresh token error
    if (typeof window !== 'undefined') {
      window.location.href = '/403';
    }
    throw error;
  }
};

interface OriginalRequestInfo {
  url: string;
  config: RequestInit;
}

export const handleApiError = async (
  error: Response,
  originalRequestInfo: OriginalRequestInfo,
  refreshToken?: string,
): Promise<Response> => {
  // Only handle 401 errors - all others should be handled by the calling code
  if (error.status !== 401) {
    throw error;
  }

  /*
   * Check for refresh token - if none exists, we can't refresh
   * Use custom refresh token if provided, otherwise use the one from cookies
   *
   * This is because we couldn't get refresh token from cookie in server side
   * and we need to pass it through the request
   */
  const _refreshToken = refreshToken || Cookies.get(CookieKey.RefreshToken);

  if (!_refreshToken) {
    Cookies.remove(CookieKey.AccessToken);
    throw error;
  }

  try {
    // Start a new refresh operation
    refreshPromise = refreshAuthToken(_refreshToken);

    try {
      // Wait for the refresh to complete
      const newToken = await refreshPromise;

      // Retry the original request with the new token
      return await fetch(originalRequestInfo.url, {
        ...originalRequestInfo.config,
        headers: {
          ...originalRequestInfo.config.headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    } finally {
      // Clear the refresh promise so future 401s will trigger a new refresh
      refreshPromise = null;
    }
  } catch (_) {
    // If anything fails, throw the original error
    throw error;
  }
};
